import React from 'react'
import { withRouter } from 'react-router-dom'

import Dropdown from 'components/Dropdown'
import InfiniteList, { handleFetchMore } from 'components/InfiniteList'
import Tooltip from 'components/Tooltip'

import { useGetPagesQuery } from 'gqlTypes'

// Prevent circular dependencies
import { useVersioning } from 'modules/versioning/ShareVersionContext/useVersioning'

import {
  DropdownContentContainer,
  DropdownIcon,
  DropdownTarget,
} from './PagesDropdown.styles'
import { RouteProps, routes } from 'constants/routes'

const ALL_PAGES_TEXT = 'All pages'

type PageRouteKey = keyof Pick<
  typeof routes,
  'SHARE_PAGE_VIEW' | 'SHARE_PAGE_CANVAS_VIEW'
>
interface PagesDropdownProps extends RouteProps<'SHARE_PAGE_VIEW'> {
  shareIdentifier: string
  collapse?: boolean
  className?: string
  alternateTargetText?: string
  showOptionAllPages?: boolean
  pageLinkRouteKey?: PageRouteKey
}

const entriesPath = ['share', 'version', 'document', 'pages', 'entries']

export const PagesDropdown: React.FC<PagesDropdownProps> = ({
  className,
  match,
  shareIdentifier,
  collapse,
  alternateTargetText,
  showOptionAllPages = true,
  pageLinkRouteKey = 'SHARE_PAGE_VIEW',
}) => {
  const { versionShortId, getPathname } = useVersioning()

  const { data, fetchMore } = useGetPagesQuery({
    variables: { shareIdentifier, versionShortId },
  })

  const pages = data?.share?.version?.document?.pages?.entries || []

  const currentPage = pages.find(({ uuid }) => uuid === match.params.pageUUID)
  const defaultTargetText = showOptionAllPages ? ALL_PAGES_TEXT : null
  const targetText =
    currentPage?.name || alternateTargetText || defaultTargetText

  const after = data?.share?.version?.document?.pages?.meta?.after || null
  const onLoadMore = handleFetchMore(fetchMore, entriesPath, {
    after,
  })

  return (
    <Dropdown
      data-testid="page-selector"
      spacing="19px"
      contentPadding="0"
      className={className}
      toggle={dropdownIsVisible => (
        <DropdownTarget>
          {!collapse ? (
            targetText
          ) : (
            <Tooltip
              content={targetText}
              placement="bottom"
              disabled={dropdownIsVisible}>
              &hellip;
            </Tooltip>
          )}
          <DropdownIcon />
        </DropdownTarget>
      )}>
      <DropdownContentContainer data-testid="page-selector-dropdown">
        <InfiniteList onLoadMore={onLoadMore}>
          {showOptionAllPages && (
            <Dropdown.Item.NavLink
              to={getPathname({
                routeKey: 'SHARE_VIEW',
                routeParams: { shareID: shareIdentifier },
              })}
              exact>
              {ALL_PAGES_TEXT}
            </Dropdown.Item.NavLink>
          )}
          {pages.map(page => (
            <Dropdown.Item.NavLink
              key={page.uuid}
              to={getPathname({
                routeKey: pageLinkRouteKey,
                routeParams: {
                  shareID: shareIdentifier,
                  pageUUID: page.uuid!,
                },
              })}
              exact>
              {page.name}
            </Dropdown.Item.NavLink>
          ))}
        </InfiniteList>
      </DropdownContentContainer>
    </Dropdown>
  )
}

export default withRouter(PagesDropdown)
